import { Box, Grid, GridItem, useToken } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import dayjs from 'dayjs';
import type React from 'react';
import { useState } from 'react';
import { MdAdd } from 'react-icons/md';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDatePicker, { getTodayInNYTimezone } from '@/components/Molecules/FaceliftDatePicker';
import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import { PatientForm } from '@/components/Templates/ManagePatients';
import type { PatientFormData } from '@/components/Templates/ManagePatients/PatientForm';
import { useGetInsurances } from '@/hooks/service-hooks/insurances';
import {
	useCreatePatient,
	useGetPatients,
	useGetPatientsAi,
} from '@/hooks/service-hooks/patients';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { FormMethods } from '@/types/form';
import type { Insurance, Patients } from '@/types/patient';
import { formatDate } from '@/utils';
import SmartInsurance from '@/components/Organisms/SmartInsurance';
import Show from '@/components/Molecules/Show';
import usePermission from '@/hooks/usePermission';

export interface PatientInformationData {
	selectPatient: string;
	dob: string;
	primaryInsurance: string;
	primaryMemberID: string;
	secondaryInsurance: string;
	secondaryMemberID: string;
	emrID: string;
	isPatientResidingInNursingHome: string;
	ifYesHasItBeenOver100Days: string;
}

export interface PatientInformationProps {
	formMethods: FormMethods<BVFormData>;
}

const yesOrNo: SelectItem[] = [
	{ value: 'no', label: 'No' },
	{ value: 'yes', label: 'Yes' },
];

const PatientInformation: React.FC<PatientInformationProps> = ({
	formMethods,
}) => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();

	const { watch, errors, setValue, setError } = formMethods;

	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | null;
		data: string | Partial<PatientFormData> | null;
	}>({
		mode: null,
		data: null,
	});

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
	};

	// Insurances with pagination
	const primaryInsurances = usePaginatedSelect({
		queryHook: useGetInsurances,
		queryParams: { enabled: true },
		transform: (insurance: Insurance) => ({
			value: insurance.ta_insuranceid,
			label: insurance.ta_name,
		}),
		errorMessage: 'Unable to load insurances',
	});

	const secondaryInsurances = usePaginatedSelect({
		queryHook: useGetInsurances,
		queryParams: { enabled: true },
		transform: (insurance: Insurance) => ({
			value: insurance.ta_insuranceid,
			label: insurance.ta_name,
		}),
		errorMessage: 'Unable to load insurances',
	});

	const patients = usePaginatedSelect({
		queryHook: useGetPatientsAi,
		queryParams: {
			clinicId: (watch('clinic') as SelectItem)?.value as string,
			enabled: !!watch('clinic'),
		},
		transform: (patient: Patients) => ({
			value: patient.contactid,
			label: patient.fullname,
			secondaryInsurance: patient.secondaryInsurance,
			primaryInsurance: patient.primaryInsurance,
			birthdate: patient.birthdate,
			ta_primarymemberid: patient.ta_primarymemberid,
			ta_secondarymemberid: patient.ta_secondarymemberid,
			ta_emrid: patient.ta_emrid,
			firstname: patient.firstname,
			lastname: patient.lastname,
		}),
		errorMessage: 'Unable to load patients',
		resetKey: ((watch('clinic') as SelectItem)?.value as string) || '',
	});

	// Mutations for create and update
	const {
		mutateAsync: createPatient,
		isLoading: createLoading,
		isSuccess: createSuccess,
	} = useCreatePatient((createdPatient: Patients, payload: any) => {
		// Create SelectItem from the created patient
		const newPatientSelectItem: SelectItem = {
			value: createdPatient.contactid,
			label: createdPatient.fullname,
		};

		// Set the newly created patient as selected
		setValue('patient', newPatientSelectItem);

		// Set all related fields
		if (createdPatient.firstname) {
			setValue('patientFirstName', createdPatient.firstname);
		}
		if (createdPatient.lastname) {
			setValue('patientLastName', createdPatient.lastname);
		}
		
		if (payload.secondaryInsurance) {
			setValue('secondaryInsurance', {
				label: payload.secondaryInsurance.label,
				value: payload.secondaryInsurance.value,
			});
		}
		
		if (payload.primaryInsurance) {
			setValue('primaryInsurance', {
				label: payload.primaryInsurance.label,
				value: payload.primaryInsurance.value,
			});
		}
		
		if (createdPatient.birthdate) {
			setValue('birthdate', createdPatient.birthdate);
		}
		if (createdPatient.ta_primarymemberid) {
			setValue('ta_primarymemberid', createdPatient.ta_primarymemberid);
		}
		if (createdPatient.ta_secondarymemberid) {
			setValue('ta_secondarymemberid', createdPatient.ta_secondarymemberid);
		}
		if (createdPatient.ta_emrid) {
			setValue('ta_emrid', createdPatient.ta_emrid);
		}

		// Clear errors for all related fields
		setError('patient', { type: 'manual', message: '' });
		setError('patientFirstName', { type: 'manual', message: '' });
		setError('patientLastName', { type: 'manual', message: '' });
		setError('birthdate', { type: 'manual', message: '' });
		setError('primaryInsurance', { type: 'manual', message: '' });
		setError('ta_primarymemberid', { type: 'manual', message: '' });

		// Close the modal
		handleCloseModal();
	});

	// Handle form submission
	const handleSubmitPatient = async (formData: PatientFormData) => {
		try {
			// Transform form data to API payload format
			const payload = {
				firstname: formData.firstname,
				ta_emrid: formData.ta_emrid,
				lastname: formData.lastname,
				gendercode: formData.gendercode,
				ta_primarymemberid: formData.ta_primarymemberid,
				preferredcontactmethodcode: formData.preferredcontactmethodcode,
				mobilephone: formData.mobilephone,
				ta_secondarymemberid: formData.ta_secondarymemberid,
				emailaddress1: formData.emailaddress1,
				address1_line1: formData.address1_line1,
				address1_city: formData.address1_city,
				address1_stateorprovince: formData.address1_stateorprovince,
				address1_postalcode: formData.address1_postalcode,
				birthdate: dayjs(formData.birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
				'ta_PrimaryInsurance@odata.bind': formData.primaryInsurance?.value
					? `ta_insurances(${formData.primaryInsurance?.value})`
					: undefined,
				'ta_SecondaryInsurance@odata.bind': formData.secondaryInsurance?.value
					? `ta_insurances(${formData.secondaryInsurance?.value})`
					: undefined,
				'parentcustomerid_account@odata.bind': formData.clinic?.value
					? `accounts(${formData.clinic?.value})`
					: undefined,
				ta_originationsource: '953870002',
				customertypecode: '100000011',
				primaryInsurance: formData.primaryInsurance,
				secondaryInsurance: formData.secondaryInsurance,
			};

			if (modalInformation.mode === 'create') {
				await createPatient(payload);
			}
		} catch (error) {
			console.error('Error submitting patient:', error);
			// Error handling is now done in the mutation hooks
		}
	};

	return (
		<>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Patient Information
			</FaceliftText>

			<Grid
				templateColumns={{
					base: '1fr',
					sm: 'repeat(1, 1fr)',
					md: 'repeat(3, 1fr)',
				}}
				gap={{ base: 4, sm: 5, md: 6 }}
				alignItems="start"
			>
				{/* Row 1 */}
				{/* Select Patient */}
				<GridItem
					display={'flex'}
					justifyContent={'space-between'}
					alignItems={'center'}
					gap={3}
				>
					<Box width={'full'}>
						<FaceliftSelect<SelectItem, false>
							limit={10}
							topLabel="Patient"
							required
							value={(watch('patient') as SelectItem)}
							onChange={(newValue: SingleValue<SelectItem>) => {
								const patientInfo = patients.items.find(
									(item) => item.value === (newValue as SelectItem)?.value,
								);
								const secondaryInsurance = patientInfo?.secondaryInsurance;
								const primaryInsurance = patientInfo?.primaryInsurance;
								const birthDate = patientInfo?.birthdate;
								setValue('patient', (newValue as SelectItem) || '', { shouldDirty: true });
								setValue('patientFirstName', patientInfo?.firstname, { shouldDirty: true });
								setValue('patientLastName', patientInfo?.lastname, { shouldDirty: true });
								if (secondaryInsurance?.ta_name) {
									setValue('secondaryInsurance', {
										label: secondaryInsurance?.ta_name,
										value: secondaryInsurance?.ta_insuranceid,
									}, { shouldDirty: true });
								}
								setValue('primaryInsurance', {
									label: primaryInsurance?.ta_name,
									value: primaryInsurance?.ta_insuranceid,
								}, { shouldDirty: true });
								setValue('birthdate', birthDate, { shouldDirty: true });
								setValue('ta_primarymemberid', patientInfo?.ta_primarymemberid, { shouldDirty: true });
								setValue(
									'ta_secondarymemberid',
									patientInfo?.ta_secondarymemberid,
									{ shouldDirty: true },
								);
								setValue('ta_emrid', patientInfo?.ta_emrid, { shouldDirty: true });

								setError('patient', { type: 'manual', message: '' });
								setError('patientFirstName', { type: 'manual', message: '' });
								setError('patientLastName', { type: 'manual', message: '' });
								setError('birthdate', { type: 'manual', message: '' });
								setError('primaryInsurance', { type: 'manual', message: '' });
								setError('ta_primarymemberid', { type: 'manual', message: '' });
							}}
							ai
							aiSuggestionText="Use a keyword like “Smith” to see more relevant results"
							items={patients.items}
							error={errors.patient?.message}
							loading={patients.loading}
							placeholder="Type Patient Name"
							onLoadMore={patients.onLoadMore}
						/>
					</Box>
					<FaceliftIconButton
						size={'2xs'}
						sx={{ marginTop: '-50px' }}
						onClick={() => {
							const clinic = watch('clinic') as SelectItem;
							const payload = {
								clinic: clinic?.value
								? {
										value: clinic?.value || '',
										label: clinic?.label || '',
									}
								: null,
							};
							setModalInformation({ mode: 'create', data: payload })
						}}
						aria-label="Add Patient"
						mb={1}
					>
						<MdAdd />
					</FaceliftIconButton>
				</GridItem>

				{/* Primary Insurance */}
				<GridItem>
					<Show when={hasPermission(['SmartInsurance:Visualize'])}>
						<SmartInsurance 
							placeholder="Search..."
							toplabel="Primary Insurance"
							required
							onConfirm={(e) => {
								if (e?.plan) {
									setValue('primaryInsurance', {
										value: e.plan.id,
										label: e.plan.name,
									}, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
								} else {
									setValue('primaryInsurance', undefined as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
								}
							}}
							defaultValue={{
								grouping: '',
								type: '',
								plan: {
									id: (watch('primaryInsurance') as SelectItem)?.value as string,
									name: (watch('primaryInsurance') as SelectItem)?.label 
								}
							}}
							error={errors.primaryInsurance?.message}
						/>
					</Show>
					<Show when={!hasPermission(['SmartInsurance:Visualize'])}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Primary Insurance"
							isClearable
							value={watch('primaryInsurance') as SelectItem | null}
							onChange={(newValue: SingleValue<SelectItem>) => {
								setValue('primaryInsurance', (newValue as SelectItem) || null, { shouldDirty: true });
								setError('primaryInsurance', { type: 'manual', message: '' });
							}}
							required
							items={primaryInsurances.items}
							loading={primaryInsurances.loading}
							error={errors?.primaryInsurance?.message}
							placeholder="Select Primary Insurance"
							onLoadMore={primaryInsurances.onLoadMore}
							ai
							aiSuggestionText="Use a keyword like “United” to see more relevant results"
						/>
					</Show>
				</GridItem>

				{/* Secondary Insurance */}
				<GridItem>
					<Show when={hasPermission(['SmartInsurance:Visualize'])}>
						<SmartInsurance 
							placeholder="Search..."
							toplabel="Secondary Insurance"
							onConfirm={(e) => {
								if (e?.plan) {
									setValue('secondaryInsurance', {
										value: e.plan.id,
										label: e.plan.name,
									}, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
								} else {
									setValue('secondaryInsurance', undefined as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
								}
							}}
							defaultValue={{
								grouping: '',
								type: '',
								plan: {
									id: (watch('secondaryInsurance') as SelectItem)?.value as string,
									name: (watch('secondaryInsurance') as SelectItem)?.label 
								}
							}}
						/>
					</Show>
					<Show when={!hasPermission(['SmartInsurance:Visualize'])}>
						<FaceliftSelect<SelectItem, false>
							limit={15}
							topLabel="Secondary Insurance"
							value={watch('secondaryInsurance') as SelectItem | null}
							onChange={(newValue: SingleValue<SelectItem>) =>
								setValue('secondaryInsurance', (newValue as SelectItem) || null, { shouldDirty: true })
							}
							items={secondaryInsurances.items}
							loading={secondaryInsurances.loading}
							// error={
							// 	insurances.error ||
							// 	errors['patientInformation.secondaryInsurance']
							// }
							placeholder="Select Secondary Insurance"
							onLoadMore={secondaryInsurances.onLoadMore}
							ai
							aiSuggestionText="Use a keyword like “United” to see more relevant results"
						/>
					</Show>
				</GridItem>

				{/* Row 2 */}
				{/* DOB */}
				<GridItem>
					<FaceliftDatePicker
						topLabel="DOB"
						value={formatDate(watch('birthdate'))}
						onChange={(date: string) => {
							setValue('birthdate', date, { shouldDirty: true });
							setError('birthdate', { type: 'manual', message: '' });
						}}
						placeholder="MM/DD/YYYY"
						required
						error={errors.birthdate?.message}
						maxDate={getTodayInNYTimezone()}
						defaultToToday
					/>
				</GridItem>

				{/* Primary Member ID */}
				<GridItem>
					<FaceliftInput
						px={3}
						topLabel="Primary Member ID"
						placeholder="Add Primary Member ID"
						value={watch('ta_primarymemberid')}
						required
						error={errors.ta_primarymemberid?.message}
						onChange={(e) => {
							setValue('ta_primarymemberid', e.target.value, { shouldDirty: true });
							setError('ta_primarymemberid', { type: 'manual', message: '' });
						}}
					/>
				</GridItem>

				{/* Secondary Member ID */}
				<GridItem>
					<FaceliftInput
						px={3}
						topLabel="Secondary Member ID"
						placeholder="Add Secondary Member ID"
						value={watch('ta_secondarymemberid')}
						onChange={(e) => setValue('ta_secondarymemberid', e.target.value, { shouldDirty: true })}
					/>
				</GridItem>

				{/* Row 3 */}
				{/* EMR ID */}
				<GridItem mt={5}>
					<FaceliftInput
						px={3}
						topLabel="EMR ID"
						placeholder="EMR ID"
						value={watch('ta_emrid')}
						onChange={(e) => setValue('ta_emrid', e.target.value, { shouldDirty: true })}
					/>
				</GridItem>

				{/* Is Patient Residing in a Nursing Home or Facility? */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={10}
						topLabel="Is Patient Residing in a Nursing Home or Nursing Facility?"
						value={
							yesOrNo.find((item) => item.value === watch('ta_nursinghome')) ||
							yesOrNo.at(0)
						}
						onChange={(newValue: SingleValue<SelectItem>) =>
							setValue('ta_nursinghome', (newValue?.value as string) || '', { shouldDirty: true })
						}
						items={yesOrNo}
						placeholder="Yes/No"
					/>
				</GridItem>

				{/* If Yes, has it been over 100 Days? */}
				<GridItem mt={5}>
					<FaceliftSelect<SelectItem, false>
						topLabel="If Yes, has it been over 100 Days?"
						value={
							yesOrNo.find((item) => item.value === watch('ta_100days')) ||
							yesOrNo.at(0)
						}
						onChange={(newValue: SingleValue<SelectItem>) =>
							setValue('ta_100days', (newValue?.value as string) || '', { shouldDirty: true })
						}
						items={yesOrNo}
						placeholder="Yes/No"
					/>
				</GridItem>
			</Grid>

			<FaceliftDialog
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) setModalInformation({ mode: null, data: null });
				}}
				title={
					modalInformation.mode === 'create' ? 'Create Patient' : 'Edit Patient'
				}
				size="xl"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
			>
				<PatientForm
					mode={modalInformation.mode as 'create' | 'edit'}
					initialData={modalInformation.data}
					onSubmit={handleSubmitPatient}
					isLoading={createLoading}
					onSuccess={() => setModalInformation({ mode: null, data: null })}
				/>
			</FaceliftDialog>
		</>
	);
};

export default PatientInformation;
